import posthog, { type PostHog, type PostHogConfig } from 'posthog-js'
import environment from '_environments'

/**
 * The shared PostHog client, initialized once for both app shells.
 *
 * Kept out of the `@/services` barrel on purpose: the barrel also exports the
 * legacy Cognito/Amplify setup and `user.service`, whose `../helpers` import
 * builds the legacy Redux store at module load. `AnalyticsProvider` is mounted
 * by the new shell too, so it imports this module directly (MAM-1011).
 */
let posthogClient: PostHog | null = null

type PostHogClientOptions = {
  /**
   * Start every page load opted out of capturing, until the app opts in. The
   * new shell passes it: it only knows whether a session is an impersonation
   * once it has signed in, and the SDK sends its first pageview, with any
   * persisted identity, right after init (MAM-1306). The legacy entry leaves
   * it off.
   */
  startOptedOut?: boolean
}

/**
 * The default opt-out only applies while consent is undecided, and the SDK
 * stores an opt-in across loads: `loaded` clears it before the first pageview
 * so each load waits for the app again.
 */
const newShellConsent: Partial<PostHogConfig> = {
  opt_out_capturing_by_default: true,
  loaded: client => {
    client.clear_opt_in_out_capturing()
  },
}

/** The first call initializes the client; later calls return it and ignore `options`. */
export const getPostHogClient = ({
  startOptedOut = false,
}: PostHogClientOptions = {}): PostHog | null => {
  if (posthogClient) {
    return posthogClient
  }

  if (!environment.keys.posthog) {
    return null
  }

  try {
    posthog.init(environment.keys.posthog, {
      api_host: 'https://spaceraid.myassetmap.com',
      ui_host: 'https://us.posthog.com',
      // Enable all features by default - AnalyticsProvider will control usage via config
      autocapture: true,
      capture_pageview: true,
      ...(startOptedOut ? newShellConsent : {}),
    })
    posthogClient = posthog
    return posthogClient
  } catch (error) {
    console.error('Failed to initialize PostHog:', error)
    return null
  }
}
